/**
 * Quarterly Project Management Utilities
 * Automatically create and manage quarterly operational expense projects
 */

import { Project } from '../types';

export interface QuarterInfo {
  quarter: number;
  year: number;
  startDate: Date;
  endDate: Date;
  label: string;
}

/**
 * Get current quarter information
 */
export function getCurrentQuarter(): QuarterInfo {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3) + 1;
  const year = now.getFullYear();
  
  return {
    quarter,
    year,
    startDate: new Date(year, (quarter - 1) * 3, 1),
    endDate: new Date(year, quarter * 3, 0),
    label: `Q${quarter} ${year}`,
  };
}

/**
 * Get next quarter information
 */
export function getNextQuarter(currentQuarter?: QuarterInfo): QuarterInfo {
  const current = currentQuarter || getCurrentQuarter();
  const nextQuarter = current.quarter === 4 ? 1 : current.quarter + 1;
  const nextYear = current.quarter === 4 ? current.year + 1 : current.year;
  
  return {
    quarter: nextQuarter,
    year: nextYear,
    startDate: new Date(nextYear, (nextQuarter - 1) * 3, 1),
    endDate: new Date(nextYear, nextQuarter * 3, 0),
    label: `Q${nextQuarter} ${nextYear}`,
  };
}

/**
 * Get previous quarter information
 */
export function getPreviousQuarter(currentQuarter?: QuarterInfo): QuarterInfo {
  const current = currentQuarter || getCurrentQuarter();
  const prevQuarter = current.quarter === 1 ? 4 : current.quarter - 1;
  const prevYear = current.quarter === 1 ? current.year - 1 : current.year;
  
  return {
    quarter: prevQuarter,
    year: prevYear,
    startDate: new Date(prevYear, (prevQuarter - 1) * 3, 1),
    endDate: new Date(prevYear, prevQuarter * 3, 0),
    label: `Q${prevQuarter} ${prevYear}`,
  };
}

/**
 * Create a quarterly operational project
 */
export function createQuarterlyProject(
  organizationId: string,
  organizationName: string,
  quarterInfo: QuarterInfo,
  customConfig?: {
    budgetCategories?: Array<{ category: string; amount: number; description: string }>;
    teamMembers?: Array<{ userId: string; role: any }>;
  }
): Project {
  const defaultBudgetCategories = [
    { category: 'Office Supplies', amount: 5000, description: 'Stationery, printing, office materials' },
    { category: 'Travel & Accommodation', amount: 20000, description: 'Business travel and lodging' },
    { category: 'Food & Catering', amount: 3000, description: 'Team meals and catering' },
    { category: 'Meetings & Events', amount: 10000, description: 'Conference rooms, events' },
    { category: 'Software Subscriptions', amount: 15000, description: 'Software licenses and SaaS' },
    { category: 'Miscellaneous', amount: 5000, description: 'Other operational expenses' },
  ];

  const budgetCategories = customConfig?.budgetCategories || defaultBudgetCategories;

  return {
    id: `proj-quarterly-${organizationId}-q${quarterInfo.quarter}-${quarterInfo.year}`,
    organizationId,
    name: `${quarterInfo.label} Operational Expenses - ${organizationName}`,
    description: `Quarterly tracking of operational expenses including office supplies, travel, food, meetings, and subscriptions for ${organizationName}.`,
    status: 'In Progress',
    proposal: `This project tracks all operational and recurring expenses for ${quarterInfo.label}. It includes budget allocation across different categories to ensure spending stays within planned limits.`,
    costs: budgetCategories.map((cat, idx) => ({
      id: `cost-${cat.category.toLowerCase().replace(/\s+/g, '-')}-${idx}`,
      category: cat.category,
      amount: cat.amount,
      description: cat.description,
    })),
    teamMembers: customConfig?.teamMembers || [],
    createdAt: quarterInfo.startDate.toISOString(),
    updatedAt: new Date().toISOString(),
    externalLinks: [],
    startDate: quarterInfo.startDate.toISOString().split('T')[0],
    targetEndDate: quarterInfo.endDate.toISOString().split('T')[0],
    metadata: {
      templateType: 'spending-tracking',
      templateId: 'spending-tracking',
      quarter: `Q${quarterInfo.quarter}`,
      year: quarterInfo.year.toString(),
      isQuarterlyOperational: true,
    },
  };
}

/**
 * Check if a quarterly project exists for the given quarter
 */
export function hasQuarterlyProject(
  projects: Project[],
  organizationId: string,
  quarterInfo: QuarterInfo
): boolean {
  return projects.some(
    (p) =>
      p.organizationId === organizationId &&
      p.metadata?.quarter === `Q${quarterInfo.quarter}` &&
      p.metadata?.year === quarterInfo.year.toString() &&
      p.metadata?.isQuarterlyOperational === true
  );
}

/**
 * Get quarterly project for the given quarter
 */
export function getQuarterlyProject(
  projects: Project[],
  organizationId: string,
  quarterInfo: QuarterInfo
): Project | null {
  return (
    projects.find(
      (p) =>
        p.organizationId === organizationId &&
        p.metadata?.quarter === `Q${quarterInfo.quarter}` &&
        p.metadata?.year === quarterInfo.year.toString() &&
        p.metadata?.isQuarterlyOperational === true
    ) || null
  );
}

/**
 * Get all quarterly projects for an organization
 */
export function getAllQuarterlyProjects(
  projects: Project[],
  organizationId: string
): Project[] {
  return projects.filter(
    (p) => p.organizationId === organizationId && p.metadata?.isQuarterlyOperational === true
  );
}

/**
 * Auto-create next quarter's project if we're in the last month of current quarter
 */
export function shouldAutoCreateNextQuarter(currentDate: Date = new Date()): boolean {
  const month = currentDate.getMonth();
  // Last month of each quarter: Feb (1), May (4), Aug (7), Nov (10)
  return month === 1 || month === 4 || month === 7 || month === 10;
}

/**
 * Calculate total budget for a quarterly project
 */
export function calculateTotalBudget(project: Project): number {
  return project.costs.reduce((sum, cost) => sum + cost.amount, 0);
}

/**
 * Calculate spent amount from tasks (if tasks track expenses)
 */
export function calculateSpentAmount(project: Project, tasks: any[]): number {
  const projectTasks = tasks.filter((t) => t.projectId === project.id);
  return projectTasks.reduce((sum, task) => sum + (task.cost || 0), 0);
}

/**
 * Get budget utilization percentage
 */
export function getBudgetUtilization(totalBudget: number, spentAmount: number): number {
  if (totalBudget === 0) return 0;
  return Math.round((spentAmount / totalBudget) * 100);
}

/**
 * Get quarter status based on dates
 */
export function getQuarterStatus(quarterInfo: QuarterInfo): 'upcoming' | 'current' | 'past' {
  const now = new Date();
  if (now < quarterInfo.startDate) return 'upcoming';
  if (now > quarterInfo.endDate) return 'past';
  return 'current';
}

/**
 * Generate quarters for a year
 */
export function generateYearQuarters(year: number): QuarterInfo[] {
  return [1, 2, 3, 4].map((quarter) => ({
    quarter,
    year,
    startDate: new Date(year, (quarter - 1) * 3, 1),
    endDate: new Date(year, quarter * 3, 0),
    label: `Q${quarter} ${year}`,
  }));
}
